import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';

import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ReservationFilters from './components/ReservationFilters';
import ReservationTable from './components/ReservationTable';
import ReservationModal from './components/ReservationModal';
import ReservationStats from './components/ReservationStats';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import NotificationModal from './components/NotificationModal';
import PaymentModal from './components/PaymentModal';
import { reservationService, dashboardService, authService } from '../../services/api';
import surveyService from '../../services/surveyService';
import { translateRoomType } from '../../constants/roomTypes';

const ReservationsPage = () => {
  const [reservations, setReservations] = useState([]);
  const [filteredReservations, setFilteredReservations] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [modalMode, setModalMode] = useState('view');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reservationToDelete, setReservationToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationData, setNotificationData] = useState({
    type: 'success',
    title: '',
    message: '',
    details: null
  });

  // Estados para modal de pagamento
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [reservationForPayment, setReservationForPayment] = useState(null);

  // Estados para modal de checkout
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [checkoutReservation, setCheckoutReservation] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [isDeferredPayment, setIsDeferredPayment] = useState(false);
  const [checkoutPaymentData, setCheckoutPaymentData] = useState({
    amount: '',
    paymentMethod: 'PIX',
    transactionId: '',
    paymentDate: new Date().toISOString().split('T')[0],
    notes: '',
    discountAmount: ''
  });

  // Estados de loading para diferentes operações
  const [loadingStates, setLoadingStates] = useState({
    checkIn: {},
    checkOut: {},
    processPayment: {},
    cancel: {},
    edit: false
  });

  // Estado do hotel selecionado
  const [selectedHotel, setSelectedHotel] = useState(null);

  // Estados para modal de check-in
  const [showCheckinModal, setShowCheckinModal] = useState(false);
  const [checkinReservation, setCheckinReservation] = useState(null);
  const [checkinLoading, setCheckinLoading] = useState(false);

  // Carregar hotel selecionado
  useEffect(() => {
    const storedHotel = localStorage.getItem('selectedHotel');
    if (storedHotel) {
      try {
        setSelectedHotel(JSON.parse(storedHotel));
      } catch (error) {
        console.error('Erro ao parsear hotel selecionado:', error);
      }
    }
  }, []);

  // Carregar dados reais
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Obter hotel selecionado
        const selectedHotel = authService.getSelectedHotel();
        console.log('🏨 Hotel selecionado:', selectedHotel);
        
        if (!selectedHotel?.id) {
          console.warn('⚠️ Nenhum hotel selecionado, não é possível carregar reservas');
          setReservations([]);
          setFilteredReservations([]);
          setStats({});
          return;
        }
        
        console.log('📋 Carregando reservas para o hotel:', selectedHotel.id);
        
        // Carregar reservas do hotel específico
        const reservationsData = await reservationService.getAll({ 
          hotelId: selectedHotel.id 
        });
        console.log('📊 Dados de reservas recebidos:', reservationsData);
        console.log('📊 Tipo de dados:', typeof reservationsData);
        console.log('📊 Estrutura completa:', JSON.stringify(reservationsData, null, 2));
        
        // O backend retorna { reservations: [...], pagination: {...} }
        const reservationsArray = reservationsData.reservations || reservationsData || [];
        console.log('🔍 Reservas filtradas por hotel:', reservationsArray.length, 'reservas');
        console.log('🔍 Primeira reserva (exemplo):', reservationsArray[0]);
        
        if (reservationsArray.length > 0) {
          console.log('🔍 Estrutura da primeira reserva:', {
            id: reservationsArray[0].id,
            guestName: reservationsArray[0].guestName,
            room: reservationsArray[0].room,
            roomId: reservationsArray[0].roomId,
            roomNumber: reservationsArray[0].roomNumber,
            roomType: reservationsArray[0].roomType
          });
        }
        
        // Verificar se cada reserva tem os dados do quarto
        const validatedReservations = reservationsArray.map(reservation => {
          console.log('🔍 Validando reserva:', reservation.id, 'Dados do quarto:', {
            room: reservation.room,
            roomId: reservation.roomId,
            roomNumber: reservation.roomNumber,
            roomType: reservation.roomType
          });
          
          // Garantir que a reserva tenha os dados do quarto
          if (!reservation.room && (reservation.roomId || reservation.roomNumber)) {
            // Se não tem room mas tem roomId, criar um objeto room básico
            const fallbackRoom = {
              id: reservation.roomId || 'unknown',
              number: reservation.roomNumber || 'N/A',
              type: reservation.roomType || 'N/A'
            };
            
            console.log('🔄 Criando fallback para quarto:', fallbackRoom);
            
            return {
              ...reservation,
              room: fallbackRoom
            };
          }
          
          // Se já tem room, verificar se tem os dados necessários
          if (reservation.room) {
            const room = {
              id: reservation.room.id || reservation.roomId || 'unknown',
              number: reservation.room.number || reservation.roomNumber || 'N/A',
              type: reservation.room.type || reservation.roomType || 'N/A'
            };
            
            console.log('✅ Quarto já existe, validando dados:', room);
            
            return {
              ...reservation,
              room: room
            };
          }
          
          // Se não tem nenhum dado do quarto, criar um objeto vazio
          console.log('⚠️ Nenhum dado do quarto encontrado para reserva:', reservation.id);
          
          return {
            ...reservation,
            room: {
              id: 'unknown',
              number: 'N/A',
              type: 'N/A'
            }
          };
        });
        
        console.log('✅ Reservas validadas:', validatedReservations);
        
        setReservations(validatedReservations);
        setFilteredReservations(validatedReservations);
        
        // Carregar estatísticas do hotel específico
        try {
          const statsData = await dashboardService.getStats({ hotelId: selectedHotel.id });
          console.log('📈 Estatísticas do hotel:', statsData);
          setStats(statsData);
        } catch (error) {
          console.error('Erro ao carregar estatísticas:', error);
          setStats({});
        }
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
        // Em caso de erro, mostrar mensagem e manter arrays vazios
        setReservations([]);
        setFilteredReservations([]);
        setStats({});
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Recarregar dados quando o hotel mudar
  useEffect(() => {
    const handleHotelChange = () => {
      // Recarregar dados quando o hotel for alterado
      reloadData(true);
    };

    // Adicionar listener para mudanças no localStorage
    window.addEventListener('storage', (e) => {
      if (e.key === 'selectedHotel') {
        handleHotelChange();
      }
    });

    return () => {
      window.removeEventListener('storage', handleHotelChange);
    };
  }, []);

  // Função para recarregar dados manualmente
  const reloadData = async (background = false) => {
    try {
      if (!background) setLoading(true);
      
      const selectedHotel = authService.getSelectedHotel();
      if (!selectedHotel?.id) {
        console.warn('⚠️ Nenhum hotel selecionado, não é possível carregar reservas');
        setReservations([]);
        setFilteredReservations([]);
        setStats({});
        return;
      }
      
      console.log('🔄 Recarregando dados para o hotel:', selectedHotel.id);
      
      // Carregar reservas do hotel específico
      const reservationsData = await reservationService.getAll({ 
        hotelId: selectedHotel.id 
      });
      console.log('📊 Dados de reservas recebidos:', reservationsData);
      
      // O backend retorna { reservations: [...], pagination: {...} }
      const reservationsArray = reservationsData.reservations || reservationsData || [];
      console.log('🔍 Reservas filtradas por hotel:', reservationsArray.length, 'reservas');
      
      // Verificar se cada reserva tem os dados do quarto
      const validatedReservations = reservationsArray.map(reservation => {
        console.log('🔍 Validando reserva (reload):', reservation.id, 'Dados do quarto:', {
          room: reservation.room,
          roomId: reservation.roomId,
          roomNumber: reservation.roomNumber,
          roomType: reservation.roomType
        });
        
        // Garantir que a reserva tenha os dados do quarto
        if (!reservation.room && (reservation.roomId || reservation.roomNumber)) {
          // Se não tem room mas tem roomId, criar um objeto room básico
          const fallbackRoom = {
            id: reservation.roomId || 'unknown',
            number: reservation.roomNumber || 'N/A',
            type: reservation.roomType || 'N/A'
          };
          
          console.log('🔄 Criando fallback para quarto (reload):', fallbackRoom);
          
          return {
            ...reservation,
            room: fallbackRoom
          };
        }
        
        // Se já tem room, verificar se tem os dados necessários
        if (reservation.room) {
          const room = {
            id: reservation.room.id || reservation.roomId || 'unknown',
            number: reservation.room.number || reservation.roomNumber || 'N/A',
            type: reservation.room.type || reservation.roomType || 'N/A'
          };
          
          console.log('✅ Quarto já existe, validando dados (reload):', room);
          
          return {
            ...reservation,
            room: room
          };
        }
        
        // Se não tem nenhum dado do quarto, criar um objeto vazio
        console.log('⚠️ Nenhum dado do quarto encontrado para reserva (reload):', reservation.id);
        
        return {
          ...reservation,
          room: {
            id: 'unknown',
            number: 'N/A',
            type: 'N/A'
          }
        };
      });
      
      console.log('✅ Reservas validadas:', validatedReservations);
      
      setReservations(validatedReservations);
      setFilteredReservations(validatedReservations);
      
      // Carregar estatísticas do hotel específico
      try {
        const statsData = await dashboardService.getStats({ hotelId: selectedHotel.id });
        console.log('📈 Estatísticas do hotel:', statsData);
        setStats(statsData);
      } catch (error) {
        console.error('Erro ao carregar estatísticas:', error);
        setStats({});
      }
    } catch (error) {
      console.error('Erro ao recarregar dados:', error);
      // Em caso de erro, manter arrays vazios
      setReservations([]);
      setFilteredReservations([]);
      setStats({});
    } finally {
      if (!background) setLoading(false);
    }
  };

  const handleFiltersChange = useCallback((filters) => {
    try {
      let filtered = [...reservations];

      // Validar e filtrar por período
      if (filters.dateRange?.start) {
        const startDate = new Date(filters.dateRange.start);
        if (!isNaN(startDate.getTime())) {
          filtered = filtered.filter(r => new Date(r.checkInDate) >= startDate);
        }
      }
      if (filters.dateRange?.end) {
        const endDate = new Date(filters.dateRange.end);
        if (!isNaN(endDate.getTime())) {
          filtered = filtered.filter(r => new Date(r.checkOutDate) <= endDate);
        }
      }

      // Filtrar por status de pagamento
      if (filters.paymentStatus && typeof filters.paymentStatus === 'string') {
        filtered = filtered.filter(r => r.paymentStatus === filters.paymentStatus);
      }

      // Filtrar por tipo de quarto
      if (filters.roomType && typeof filters.roomType === 'string') {
        filtered = filtered.filter(r => (r.room?.type || r.roomType || '').toLowerCase() === filters.roomType);
      }

      // Filtrar por busca de hóspede (sanitizar entrada)
      if (filters.guestSearch && typeof filters.guestSearch === 'string') {
        const searchTerm = filters.guestSearch.toLowerCase().replace(/[<>]/g, '');
        if (searchTerm.length > 0) {
          filtered = filtered.filter(r => 
            r.guestName?.toLowerCase().includes(searchTerm) ||
            r.documentNumber?.includes(searchTerm) ||
            r.guestEmail?.toLowerCase().includes(searchTerm)
          );
        }
      }

      setFilteredReservations(filtered);
    } catch (error) {
      console.error('Erro ao aplicar filtros:', error);
      setFilteredReservations(reservations);
    }
  }, [reservations]);

  const handleClearFilters = () => {
    setFilteredReservations(reservations);
  };

  const handleCreateReservation = useCallback(() => {
    setSelectedReservation(null);
    setModalMode('create');
    setShowModal(true);
  }, []);

  // Memoizar cálculos das estatísticas
  const statsData = useMemo(() => {
    const totalReservations = reservations.length;
    const activeReservations = reservations.filter(r => r.status === 'CONFIRMED' || r.status === 'CHECKED_IN').length;
    const pendingCheckins = reservations.filter(r => r.status === 'CONFIRMED' && new Date(r.checkInDate) <= new Date()).length;
    const pendingCheckouts = reservations.filter(r => r.status === 'CHECKED_IN' && new Date(r.checkOutDate) <= new Date()).length;
    
    const totalRevenue = reservations.reduce((sum, r) => sum + parseFloat(r.totalAmount || 0), 0);
    const averageStay = totalReservations > 0 ? 
      reservations.reduce((sum, r) => {
        const checkIn = new Date(r.checkInDate);
        const checkOut = new Date(r.checkOutDate);
        return sum + Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
      }, 0) / totalReservations : 0;

    const formatCurrency = (price) => {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(price);
    };

    return {
      totalReservations,
      activeReservations,
      pendingCheckins,
      pendingCheckouts,
      totalRevenue,
      averageStay,
      formatCurrency
    };
  }, [reservations]);

  const handleEditReservation = (reservation) => {
    console.log('🔍 handleEditReservation - Dados da reserva:', reservation);
    console.log('🔍 handleEditReservation - Objeto room:', reservation.room);
    console.log('🔍 handleEditReservation - Room ID:', reservation.roomId);
    console.log('🔍 handleEditReservation - Room Number:', reservation.roomNumber);
    console.log('🔍 handleEditReservation - Room Type:', reservation.roomType);
    
    setSelectedReservation(reservation);
    setModalMode('edit');
    setShowModal(true);
    // Ativar loading de edição
    setLoadingStates(prev => ({ ...prev, edit: true }));
  };

  const handleViewDetails = (reservation) => {
    console.log('🔍 handleViewDetails - Dados da reserva:', reservation);
    console.log('🔍 handleViewDetails - Objeto room:', reservation.room);
    console.log('🔍 handleViewDetails - Room ID:', reservation.roomId);
    console.log('🔍 handleViewDetails - Room Number:', reservation.roomNumber);
    console.log('🔍 handleViewDetails - Room Type:', reservation.roomType);
    
    setSelectedReservation(reservation);
    setModalMode('view');
    setShowModal(true);
  };

  const handleCheckIn = async (reservation) => {
    // Abrir modal de confirmação
    setCheckinReservation(reservation);
    setShowCheckinModal(true);
  };

  const confirmCheckIn = async () => {
    if (!checkinReservation) return;
    
    try {
      setCheckinLoading(true);
      const selectedHotel = authService.getSelectedHotel();
      // Usar endpoint específico de check-in que atualiza status da reserva E do quarto
      await reservationService.checkIn(checkinReservation.id, { hotelId: selectedHotel?.id });

      // Atualizar estado local
      setReservations(prev => prev.map(r => r.id === checkinReservation.id ? { ...r, status: 'CHECKED_IN' } : r));
      setFilteredReservations(prev => prev.map(r => r.id === checkinReservation.id ? { ...r, status: 'CHECKED_IN' } : r));

      // Fechar modal
      setShowCheckinModal(false);
      setCheckinReservation(null);
      
      showNotificationModal('success', 'Check-in realizado!', 'Chegada confirmada com sucesso.');
      await reloadData(true);
    } catch (error) {
      console.error('Erro ao realizar check-in:', error);
      showNotificationModal('error', 'Erro no Check-in', 'Não foi possível confirmar a chegada.');
    } finally {
      setCheckinLoading(false);
    }
  };

  const handleCheckOut = async (reservation) => {
    console.log('🔍 Abrindo modal de checkout para reserva:', reservation);
    
    // Configurar dados da reserva
    setCheckoutReservation(reservation);
    setIsDeferredPayment(false);
    
    // Calcular valor considerando desconto da reserva
    const originalAmount = Number(reservation.totalAmount || 0);
    const reservationDiscount = reservation.discountAmount 
      ? (typeof reservation.discountAmount === 'string' 
        ? parseFloat(reservation.discountAmount) 
        : Number(reservation.discountAmount)) 
      : 0;
    const finalAmount = originalAmount - reservationDiscount;
    
    // Inicializar dados de pagamento com o valor total
    setCheckoutPaymentData({
      amount: finalAmount > 0 ? finalAmount : originalAmount,
      paymentMethod: 'PIX',
      transactionId: '',
      paymentDate: new Date().toISOString().split('T')[0],
      notes: '',
      discountAmount: ''
    });
    
    // Abrir modal
    setShowCheckoutModal(true);
  };

  // Confirmar checkout com pagamento integrado
  const confirmCheckout = async () => {
    try {
      setCheckoutLoading(true);
      console.log('🔍 Confirmando checkout para reserva:', checkoutReservation);
      console.log('🔍 ID da reserva:', checkoutReservation?.id);
      
      if (!checkoutReservation || !checkoutReservation.id) {
        showNotificationModal('error', 'Erro', 'Reserva não encontrada.');
        return;
      }
      
      console.log('✅ Passou validação de reserva');

      // Verificar status de pagamento
      const paymentAlreadyPaid = checkoutReservation.paymentStatus === 'PAID';
      
      const selectedHotel = authService.getSelectedHotel();
      
      // Preparar dados para atualização
      const updateData = {
        status: 'CHECKED_OUT'
      };
      
      // Se pagamento está pendente e não for faturado, validar e incluir dados de pagamento
      if (!paymentAlreadyPaid && !isDeferredPayment) {
        // Validar dados de pagamento
        if (!checkoutPaymentData.amount || parseFloat(checkoutPaymentData.amount) <= 0) {
          showNotificationModal('error', 'Valor Inválido', 'Por favor, informe o valor do pagamento.');
          setCheckoutLoading(false);
          return;
        }
        
        if (!checkoutPaymentData.paymentMethod) {
          showNotificationModal('error', 'Forma de Pagamento', 'Por favor, selecione a forma de pagamento.');
          setCheckoutLoading(false);
          return;
        }
        
        // Adicionar status de pagamento
        updateData.paymentStatus = 'PAID';
        
        // Calcular desconto total (da reserva + adicional no checkout)
        const reservationDiscount = checkoutReservation.discountAmount 
          ? (typeof checkoutReservation.discountAmount === 'string' 
            ? parseFloat(checkoutReservation.discountAmount) 
            : Number(checkoutReservation.discountAmount)) 
          : 0;
        
        const checkoutDiscount = checkoutPaymentData.discountAmount 
          ? (typeof checkoutPaymentData.discountAmount === 'string' 
            ? parseFloat(checkoutPaymentData.discountAmount.replace(',', '.')) || 0
            : Number(checkoutPaymentData.discountAmount)) 
          : 0;
        
        const totalDiscount = reservationDiscount + checkoutDiscount;
        
        if (totalDiscount > 0) {
          updateData.discountAmount = totalDiscount;
        }
      } else if (isDeferredPayment) {
        // Se for checkout faturado, manter status como PENDING
        updateData.paymentStatus = 'PENDING';
        
        // Salvar observação de faturamento no specialRequests da reserva, se preenchida
        if (checkoutPaymentData.notes && checkoutPaymentData.notes.trim() !== '') {
          const existingNotes = checkoutReservation.specialRequests || '';
          updateData.specialRequests = existingNotes.trim() !== ''
            ? `${existingNotes}\n[Checkout Faturado]: ${checkoutPaymentData.notes}`
            : `[Checkout Faturado]: ${checkoutPaymentData.notes}`;
        }
      }
      
      // Atualizar reserva
      console.log('📝 Atualizando reserva...');
      await reservationService.update(checkoutReservation.id, updateData, { 
        hotelId: selectedHotel?.id 
      });
      console.log('✅ Reserva atualizada com sucesso!');

      // Obter nome do hotel selecionado
      const selectedHotelData = authService.getSelectedHotel();
      const hotelName = selectedHotelData?.name || 'Hotel';
      
      // Preparar detalhes para modal de sucesso
      const successDetails = [
        { label: 'Código', value: checkoutReservation.reservationCode },
        { label: 'Hóspede', value: checkoutReservation.guestName },
        { label: 'Quarto', value: checkoutReservation.room?.number || checkoutReservation.roomNumber || 'N/A' },
        { label: 'Status', value: 'Checkout realizado' },
        { label: 'Hotel', value: hotelName }
      ];
      
      // Se processou pagamento, adicionar detalhes
      if (!paymentAlreadyPaid && !isDeferredPayment) {
        // Calcular valor final considerando desconto total (reserva + checkout)
        const originalAmount = Number(checkoutReservation.totalAmount || 0);
        const reservationDiscount = checkoutReservation.discountAmount 
          ? (typeof checkoutReservation.discountAmount === 'string' 
            ? parseFloat(checkoutReservation.discountAmount) 
            : Number(checkoutReservation.discountAmount)) 
          : 0;
        const checkoutDiscount = checkoutPaymentData.discountAmount 
          ? (typeof checkoutPaymentData.discountAmount === 'string' 
            ? parseFloat(checkoutPaymentData.discountAmount.replace(',', '.')) || 0
            : Number(checkoutPaymentData.discountAmount)) 
          : 0;
        const totalDiscount = reservationDiscount + checkoutDiscount;
        const finalAmount = originalAmount - totalDiscount;
        
        successDetails.push(
          { label: 'Valor', value: `R$ ${(finalAmount > 0 ? finalAmount : originalAmount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
          { label: 'Método', value: getPaymentMethodLabel(checkoutPaymentData.paymentMethod) },
          { label: 'Data', value: new Date(checkoutPaymentData.paymentDate).toLocaleDateString('pt-BR') }
        );
        
        if (checkoutPaymentData.transactionId) {
          successDetails.push({ label: 'Transação', value: checkoutPaymentData.transactionId });
        }
      } else if (isDeferredPayment) {
        successDetails.push({ label: 'Pagamento', value: 'Faturado (Pendente)' });
        if (checkoutPaymentData.notes && checkoutPaymentData.notes.trim() !== '') {
          successDetails.push({ label: 'Obs. Faturamento', value: checkoutPaymentData.notes });
        }
      }

      // Gerar link de avaliação - ESPERAR um pouco para garantir que a reserva foi salva
      console.log('🔍 ========== INICIANDO GERAÇÃO DE LINK ==========');
      console.log('🔍 Reservation ID:', checkoutReservation.id);
      console.log('🔍 Aguardando 1 segundo antes de gerar link...');
      
      // Aguardar 1 segundo para garantir que a reserva foi salva
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      try {
        console.log('🎯 Tentando gerar link de avaliação para reserva:', checkoutReservation.id);
        
        const invitation = await surveyService.generateSurveyInvitation(checkoutReservation.id);
        console.log('✅ Link gerado com sucesso:', invitation);
        
        if (invitation?.uniqueToken) {
          const surveyLink = `${window.location.origin}/survey/${invitation.uniqueToken}`;
          console.log('🔗 Survey Link completo:', surveyLink);
          
          successDetails.push({ 
            label: 'Link de Avaliação', 
            value: surveyLink,
            isCopyable: true,
            guestPhone: checkoutReservation.guestPhone,
            hotelName: hotelName,
            guestName: checkoutReservation.guestName
          });
          
          console.log('✅ Link adicionado ao successDetails:', successDetails);
        } else {
          console.warn('⚠️ Invitation retornado mas sem uniqueToken:', invitation);
        }
      } catch (error) {
        console.error('❌ ERRO AO GERAR LINK DE AVALIAÇÃO:', error);
        console.error('❌ Detalhes do erro:', error.response?.data || error.message);
        // Não bloquear o checkout se falhar a geração do link
      }
      
      console.log('🔍 ========== FINALIZANDO GERAÇÃO DE LINK ==========');

      // Fechar modal de checkout
      setShowCheckoutModal(false);
      setCheckoutReservation(null);
      
      // Mostrar modal de sucesso
      showNotificationModal(
        'success',
        'Checkout Realizado!',
        !paymentAlreadyPaid && !isDeferredPayment 
          ? 'Pagamento confirmado e checkout realizado com sucesso!' 
          : isDeferredPayment
            ? 'Checkout realizado e quarto liberado! O pagamento continuará pendente (faturado).'
            : 'Checkout realizado com sucesso!',
        successDetails
      );
      
      // Recarregar dados
      await reloadData(true);
      
    } catch (error) {
      console.error('Erro ao processar checkout:', error);
      
      let errorMessage = 'Erro ao processar checkout. Tente novamente.';
      
      if (error.message?.includes('timeout')) {
        errorMessage = 'Tempo limite excedido. Verifique sua conexão e tente novamente.';
      } else if (error.message?.includes('network')) {
        errorMessage = 'Erro de conexão. Verifique sua internet e tente novamente.';
      }
      
      showNotificationModal('error', 'Erro no Checkout', errorMessage);
    } finally {
      setCheckoutLoading(false);
    }
  };

  // Função auxiliar para traduzir forma de pagamento
  const getPaymentMethodLabel = (method) => {
    const labels = {
      'CASH': 'Dinheiro',
      'CREDIT_CARD': 'Cartão de Crédito',
      'DEBIT_CARD': 'Cartão de Débito',
      'PIX': 'PIX',
      'BANK_TRANSFER': 'Transferência Bancária'
    };
    return labels[method] || method;
  };

  const handleProcessPayment = async (reservation) => {
    // Abrir modal de pagamento
    setReservationForPayment(reservation);
    setShowPaymentModal(true);
  };

  const handleProcessPaymentSubmit = async (reservationId, paymentData) => {
    try {
      console.log('🔍 Processando pagamento para reserva:', reservationId);
      console.log('🔍 paymentData completo:', paymentData);
      console.log('🔍 paymentData.discountAmount:', paymentData.discountAmount);
      console.log('🔍 typeof paymentData.discountAmount:', typeof paymentData.discountAmount);
      
      // Ativar loading para esta reserva específica
      setLoadingStates(prev => ({
        ...prev,
        processPayment: { ...prev.processPayment, [reservationId]: true }
      }));
      
      // Simular delay de processamento
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Preparar dados para atualizar a reserva
      const updateData = {
        paymentStatus: 'PAID'
      };
      
      console.log('🔍 Iniciando cálculo de desconto...');
      
      // Calcular desconto total (da reserva + adicional no pagamento)
      const reservation = reservationForPayment;
      console.log('🔍 reservation:', reservation);
      console.log('🔍 reservation?.discountAmount:', reservation?.discountAmount);
      
      const reservationDiscount = reservation?.discountAmount 
        ? (typeof reservation.discountAmount === 'string' 
          ? parseFloat(reservation.discountAmount) 
          : Number(reservation.discountAmount)) 
        : 0;
      
      console.log('🔍 paymentData:', paymentData);
      console.log('🔍 paymentData.discountAmount:', paymentData.discountAmount);
      
      const paymentDiscount = paymentData.discountAmount 
        ? (typeof paymentData.discountAmount === 'string' 
          ? parseFloat(paymentData.discountAmount) 
          : Number(paymentData.discountAmount)) 
        : 0;
      
      const totalDiscount = reservationDiscount + paymentDiscount;
      
      console.log('🔍 Valores calculados:', {
        reservationDiscount,
        paymentDiscount,
        totalDiscount
      });
      
      if (totalDiscount > 0) {
        updateData.discountAmount = totalDiscount;
        console.log('✅ Desconto aplicado ao updateData:', updateData);
      } else {
        console.log('⚠️ Nenhum desconto para aplicar');
      }
      
      console.log('🔍 updateData final:', updateData);

      // Atualizar status da reserva para PAID
      const selectedHotel = authService.getSelectedHotel();
      const response = await reservationService.update(reservationId, updateData, { hotelId: selectedHotel?.id });
      
      console.log('📡 Resposta da API:', '/reservations/' + reservationId);
      console.log(response);

      // Se a resposta incluir a reserva atualizada, atualizar o estado local
      if (response && (response.id || response.reservation)) {
        const updatedReservation = response.reservation || response;
        
        setReservations(prev => 
          prev.map(r => r.id === reservationId ? updatedReservation : r)
        );
        setFilteredReservations(prev => 
          prev.map(r => r.id === reservationId ? updatedReservation : r)
        );
      }

      // Mostrar notificação de sucesso
      const details = [
        { label: 'Hóspede', value: reservationForPayment.guestName },
        { label: 'Valor', value: `R$ ${paymentData.amount.toLocaleString()}` },
        { label: 'Método', value: getPaymentMethodLabel(paymentData.paymentMethod) },
        { label: 'Status', value: getPaymentStatusLabel(paymentData.status) },
        { label: 'Data', value: paymentData.paymentDate.toLocaleDateString('pt-BR') }
      ];

      if (paymentData.transactionId) {
        details.push({ label: 'Transação', value: paymentData.transactionId });
      }

      showNotificationModal('success', 'Pagamento Processado!', 
        'Pagamento processado com sucesso!', details);

      // Recarregar dados
      await reloadData(true);

    } catch (error) {
      console.error('Erro ao processar pagamento:', error);
      showNotificationModal('error', 'Erro no Pagamento', 
        'Erro ao processar pagamento. Tente novamente.');
    } finally {
      // Desativar loading para esta reserva específica
      setLoadingStates(prev => ({
        ...prev,
        processPayment: { ...prev.processPayment, [reservationId]: false }
      }));
    }
  };

  // Função auxiliar para traduzir status de pagamento
  const getPaymentStatusLabel = (status) => {
    const labels = {
      'PENDING': 'Pendente',
      'PAID': 'Pago',
      'OVERDUE': 'Em Atraso',
      'CANCELLED': 'Cancelado',
      'REFUNDED': 'Reembolsado'
    };
    return labels[status] || status;
  };

  const handleDeleteReservation = async (reservation) => {
    setReservationToDelete(reservation);
    setShowDeleteModal(true);
  };

  const handleCancelReservation = async (reservation) => {
    if (!reservation?.id) return;
    const confirmed = window.confirm(`Cancelar a reserva ${reservation.reservationCode || ''}?`);
    if (!confirmed) return;

    setLoadingStates(prev => ({
      ...prev,
      cancel: { ...prev.cancel, [reservation.id]: true }
    }));

    try {
      const selectedHotel = authService.getSelectedHotel();
      await reservationService.update(reservation.id, { status: 'CANCELLED' }, { hotelId: selectedHotel?.id });

      setReservations(prev => prev.map(r => r.id === reservation.id ? { ...r, status: 'CANCELLED' } : r));
      setFilteredReservations(prev => prev.map(r => r.id === reservation.id ? { ...r, status: 'CANCELLED' } : r));

      showNotificationModal('success', 'Reserva Cancelada', 'A reserva foi cancelada com sucesso.', [
        { label: 'Hóspede', value: reservation.guestName },
        { label: 'Quarto', value: reservation.room?.number || reservation.roomNumber || 'N/A' }
      ]);

      await reloadData(true);
    } catch (error) {
      console.error('Erro ao cancelar reserva:', error);
      let message = 'Erro ao cancelar a reserva. Tente novamente.';
      if (error.response?.data?.message) {
        message = error.response.data.message;
      }
      showNotificationModal('error', 'Erro ao Cancelar', message);
    } finally {
      setLoadingStates(prev => ({
        ...prev,
        cancel: { ...prev.cancel, [reservation.id]: false }
      }));
    }
  };

  const handleConfirmDelete = async () => {
    if (!reservationToDelete) return;

    setIsDeleting(true);
    
    try {
      const selectedHotel = authService.getSelectedHotel();
      const response = await reservationService.delete(reservationToDelete.id, { hotelId: selectedHotel?.id });
      
      // Remover a reserva excluída do estado local
      setReservations(prev => prev.filter(r => r.id !== reservationToDelete.id));
      setFilteredReservations(prev => prev.filter(r => r.id !== reservationToDelete.id));
      
      // Fechar modal e limpar estados
      setShowDeleteModal(false);
      setReservationToDelete(null);
      
      // Mostrar mensagem de sucesso
      const details = [
        { label: 'Hóspede', value: reservationToDelete.guestName },
        { label: 'Quarto Liberado', value: response.roomNumber },
        { label: 'Status do Quarto', value: response.roomStatus }
      ];
      
      showNotificationModal('success', 'Reserva Excluída!', 'Reserva excluída com sucesso!', details);
      
      // Recarregar dados para garantir que a lista esteja atualizada
      await reloadData(true);
    } catch (error) {
      console.error('Erro ao excluir reserva:', error);
      
      // Mostrar mensagem de erro específica se disponível
      let errorMessage = 'Erro ao excluir reserva. Tente novamente.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      showNotificationModal('error', 'Erro ao Excluir', errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setReservationToDelete(null);
    setIsDeleting(false);
  };

  const handleSaveReservation = async (reservationData) => {
    try {
      console.log('🔍 handleSaveReservation - Dados recebidos:', reservationData);
      const selectedHotel = authService.getSelectedHotel();
      console.log('🔍 handleSaveReservation - Hotel selecionado:', selectedHotel);
      let response;
      
      if (modalMode === 'create') {
        console.log('🔍 handleSaveReservation - Criando nova reserva...');
        response = await reservationService.create(reservationData, { hotelId: selectedHotel?.id });
        console.log('🔍 handleSaveReservation - Resposta da criação:', response);
      } else if (modalMode === 'edit') {
        console.log('🔍 handleSaveReservation - Editando reserva:', selectedReservation.id);
        response = await reservationService.update(selectedReservation.id, reservationData, { hotelId: selectedHotel?.id });
        console.log('🔍 handleSaveReservation - Resposta da edição:', response);
      }
      
      // Se a resposta incluir a reserva atualizada, atualizar o estado local
      if (response && (response.id || response.reservation)) {
        const updatedReservation = response.reservation || response;
        
        if (modalMode === 'create') {
          // Adicionar nova reserva ao estado
          setReservations(prev => [updatedReservation, ...prev]);
          setFilteredReservations(prev => [updatedReservation, ...prev]);
        } else if (modalMode === 'edit') {
          // Atualizar reserva existente no estado
          setReservations(prev => 
            prev.map(r => r.id === selectedReservation.id ? updatedReservation : r)
          );
          setFilteredReservations(prev => 
            prev.map(r => r.id === selectedReservation.id ? updatedReservation : r)
          );
        }
      }
      
      // Retornar a reserva criada/atualizada para o modal
      return response?.reservation || response;
      
      // Não recarregar dados aqui; o reload será feito apenas ao confirmar/fechar o modal
      // Não fechar o modal aqui; o próprio modal exibirá a confirmação e fechará após um breve delay
    } catch (error) {
      console.error('❌ Erro ao salvar reserva:', error);
      console.error('❌ Detalhes do erro:', {
        message: error.message,
        response: error.response,
        responseData: error.response?.data,
        status: error.response?.status
      });
      
      let errorMessage = 'Erro ao salvar reserva. Tente novamente.';
      let errorDetails = [];
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
        
        // Se houver informações de conflito, adicionar aos detalhes
        if (error.response?.data?.conflict) {
          const conflict = error.response.data.conflict;
          errorDetails.push({
            label: 'Quarto ocupado até',
            value: conflict.availableAfter
          });
          errorMessage = `${errorMessage}. O quarto estará disponível após ${conflict.availableAfter}.`;
        }
      } else if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        errorMessage = 'Erro de validação nos dados';
        errorDetails = error.response.data.errors.map(e => ({
          label: e.param || e.path,
          value: e.msg || e.message
        }));
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showNotificationModal('error', 'Erro ao Salvar', errorMessage, errorDetails.length > 0 ? errorDetails : null);
      throw error; // Re-lançar o erro para que o modal possa tratá-lo e não exibir mensagem de sucesso prematuramente
    } finally {
      // Desativar loading de edição
      setLoadingStates(prev => ({ ...prev, edit: false }));
    }
  };

  // Quick Actions handlers
  const handleImportReservations = () => {
    // Create a hidden input element to trigger file selection
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,.xlsx,.xls';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        // Show success notification (in a real app, you'd process the file)
        showNotificationModal('info', 'Arquivo Selecionado', `Arquivo "${file.name}" selecionado para importação. Funcionalidade de importação seria implementada aqui.`);
      }
    };
    input.click();
  };

  const handleToggleAdvancedFilters = () => {
    // In a real implementation, this would toggle advanced filter panel
    showNotificationModal('info', 'Filtros Avançados', 'Filtros avançados seriam exibidos/ocultados aqui. Esta funcionalidade pode ser expandida no futuro.');
  };

  const handleExportReservations = useCallback(() => {
    try {
      // Sanitizar dados antes da exportação
      const sanitizedData = filteredReservations.map(reservation => ({
        id: reservation.id,
        guestName: reservation.guestName?.replace(/[<>]/g, ''),
        guestEmail: reservation.guestEmail?.replace(/[<>]/g, ''),
        guestPhone: reservation.guestPhone?.replace(/[<>]/g, ''),
        checkInDate: reservation.checkInDate,
        checkOutDate: reservation.checkOutDate,
        totalAmount: reservation.totalAmount,
        status: reservation.status,
        roomNumber: reservation.room?.number || 'N/A',
        roomType: reservation.room?.type || 'N/A'
      }));

      const dataStr = JSON.stringify(sanitizedData, null, 2);
      const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
      
      const exportFileDefaultName = `reservas-${new Date().toISOString().split('T')[0]}.json`;
      
      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileDefaultName);
      linkElement.click();
      
      showNotificationModal('success', 'Exportação Concluída', `${filteredReservations.length} reservas exportadas com sucesso!`);
    } catch (error) {
      console.error('Erro ao exportar reservas:', error);
      showNotificationModal('error', 'Erro na Exportação', 'Erro ao exportar reservas. Tente novamente.');
    }
  }, [filteredReservations]);

  const showNotificationModal = (type, title, message, details = null) => {
    setNotificationData({
      type,
      title,
      message,
      details
    });
    setShowNotification(true);
  };

  const handleCloseNotification = () => {
    setShowNotification(false);
    
    // Recarregar dados ao fechar modal de notificação (após usuário clicar em "Entendi")
    reloadData(true);
    
    setNotificationData({
      type: 'success',
      title: '',
      message: '',
      details: null
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header 
          onCreateReservation={handleCreateReservation} 
          onMenuClick={() => setIsMobileMenuOpen(true)}
        />
        <Sidebar 
          isOpen={isMobileMenuOpen} 
          onClose={() => setIsMobileMenuOpen(false)}
        />
        <main className="lg:ml-60 pt-16">
          <div className="p-6">
            <div className="flex items-center justify-center h-96">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header 
        onCreateReservation={handleCreateReservation} 
        onMenuClick={() => setIsMobileMenuOpen(true)}
      />
      <Sidebar 
        isOpen={isMobileMenuOpen} 
        onClose={() => setIsMobileMenuOpen(false)}
      />
      
      <main className="lg:ml-60 pt-16">
        <div className="p-4 sm:p-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb />
            
            {/* Page Header */}
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 mb-8">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl font-bold text-foreground">Gestão de Reservas</h1>
                  {selectedHotel && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                      {selectedHotel.name}
                    </span>
                  )}
                </div>
                <p className="text-muted-foreground text-sm max-w-2xl">
                  Gerencie check-ins, check-outs, pagamentos e todas as operações relacionadas às reservas
                </p>
              </div>
              
              <div className="flex flex-wrap items-center gap-3">
                {/* Quick Actions */}
                <div className="flex items-center bg-muted/30 rounded-lg p-1">
                  <button
                    onClick={handleExportReservations}
                    className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all"
                    title="Exportar Reservas"
                  >
                    <Icon name="Download" size={16} />
                    <span className="hidden sm:inline">Exportar</span>
                  </button>
                  <button
                    onClick={handleImportReservations}
                    className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all"
                    title="Importar Reservas"
                  >
                    <Icon name="Upload" size={16} />
                    <span className="hidden sm:inline">Importar</span>
                  </button>
                </div>

                <button
                  onClick={handleCreateReservation}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-all duration-200 shadow-sm hover:shadow-md active:scale-95"
                >
                  <Icon name="Plus" size={18} />
                  <span>Nova Reserva</span>
                </button>
              </div>
            </div>

            {/* Main Stats */}
            <div className="mb-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {(() => {
                  const stats = [
                    {
                      title: 'Total de Reservas',
                      value: statsData.totalReservations,
                      icon: 'Calendar',
                      color: 'primary',
                      trend: '+12%',
                      description: `${statsData.activeReservations} ativas`
                    },
                    {
                      title: 'Check-ins Pendentes',
                      value: statsData.pendingCheckins,
                      icon: 'LogIn',
                      color: 'warning',
                      trend: '+8%',
                      description: 'Aguardando chegada'
                    },
                    {
                      title: 'Check-outs Pendentes',
                      value: statsData.pendingCheckouts,
                      icon: 'LogOut',
                      color: 'destructive',
                      trend: '+5%',
                      description: 'Aguardando saída'
                    },
                    {
                      title: 'Receita Total',
                      value: statsData.formatCurrency(statsData.totalRevenue),
                      icon: 'DollarSign',
                      color: 'success',
                      trend: '+15%',
                      description: `Média: ${statsData.averageStay.toFixed(1)} dias`
                    }
                  ];

                  return stats.map((stat, index) => (
                    <div 
                      key={index} 
                      className="group relative bg-gradient-to-br from-card to-card/50 border border-border rounded-xl p-5 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 overflow-hidden"
                    >
                      <div className={`absolute top-0 right-0 w-32 h-32 bg-${stat.color}/5 rounded-full blur-3xl opacity-20 -mr-16 -mt-16 group-hover:opacity-30 transition-opacity`}></div>
                      
                      <div className="relative">
                        <div className="flex items-start justify-between mb-4">
                          <div className={`p-2.5 rounded-xl bg-${stat.color}/10 group-hover:scale-110 transition-transform duration-300`}>
                            <Icon name={stat.icon} size={22} className={`text-${stat.color}`} />
                          </div>
                          <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-success/10">
                            <Icon name="TrendingUp" size={12} className="text-success" />
                            <span className="text-xs font-semibold text-success">{stat.trend}</span>
                          </div>
                        </div>
                        
                        <div>
                          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                            {stat.title}
                          </h3>
                          <p className="text-2xl font-bold text-foreground">
                            {stat.value}
                          </p>
                          <p className="text-xs text-muted-foreground leading-relaxed pt-1 border-t border-border/50">
                            {stat.description}
                          </p>
                        </div>
                        
                        <div className="absolute inset-0 bg-gradient-to-t from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-xl pointer-events-none"></div>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>

            {/* Filters */}
            <div className="mb-6">
              <ReservationFilters
                onFiltersChange={handleFiltersChange}
                onClearFilters={handleClearFilters}
              />
            </div>

            {/* Results Summary */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 p-4 bg-gradient-to-r from-muted/20 to-muted/10 rounded-lg border border-border/50">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Icon name="Search" size={18} className="text-primary" />
                  <p className="text-sm font-medium text-foreground">
                    Mostrando <span className="text-primary font-bold">{filteredReservations.length}</span> de <span className="font-bold">{reservations.length}</span> reservas
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground">Ordenar por:</span>
                <select className="text-sm border border-border rounded-lg px-3 py-1.5 bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-primary hover:border-primary/30 transition-all">
                  <option value="date">Data de criação</option>
                  <option value="checkin">Check-in</option>
                  <option value="guest">Nome do hóspede</option>
                  <option value="amount">Valor</option>
                </select>
              </div>
            </div>

            {/* Reservations Table */}
            {filteredReservations.length > 0 ? (
              <>
                <div className="bg-card border border-border rounded-xl overflow-hidden shadow-sm">
                  <ReservationTable
                    reservations={filteredReservations}
                    onEdit={handleEditReservation}
                    onViewDetails={handleViewDetails}
                    onCheckIn={handleCheckIn}
                    onCheckOut={handleCheckOut}
                    onProcessPayment={handleProcessPayment}
                    onCancel={handleCancelReservation}
                    onDelete={handleDeleteReservation}
                    loadingStates={loadingStates}
                  />
                </div>

                {/* Pagination */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-6 p-4 bg-gradient-to-r from-muted/20 to-muted/10 rounded-lg border border-border/50">
                  <div className="text-sm text-muted-foreground">
                    Página 1 de 1 • {filteredReservations.length} reservas
                  </div>
                  <div className="flex items-center gap-2">
                    <button 
                      className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-border bg-card text-muted-foreground hover:text-foreground hover:bg-accent transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled
                      title="Página anterior"
                    >
                      <Icon name="ChevronLeft" size={16} />
                    </button>
                    <button className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-primary text-primary-foreground font-medium">
                      1
                    </button>
                    <button 
                      className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-border bg-card text-muted-foreground hover:text-foreground hover:bg-accent transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled
                      title="Próxima página"
                    >
                      <Icon name="ChevronRight" size={16} />
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="relative bg-gradient-to-br from-card to-card/50 border border-border rounded-xl p-12 text-center overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl opacity-20 -mr-32 -mt-32"></div>
                <div className="relative">
                  <div className="w-20 h-20 mx-auto mb-6 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name="Calendar" size={40} className="text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-2">Nenhuma reserva encontrada</h3>
                  <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                    Tente ajustar os filtros de busca ou crie uma nova reserva para começar
                  </p>
                  <div className="flex flex-wrap justify-center gap-3">
                    <button
                      onClick={handleClearFilters}
                      className="inline-flex items-center gap-2 px-4 py-2.5 bg-card text-foreground font-medium rounded-lg border border-border hover:bg-accent transition-all"
                    >
                      <Icon name="RotateCcw" size={18} />
                      <span>Limpar Filtros</span>
                    </button>
                    <button
                      onClick={handleCreateReservation}
                      className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-all shadow-sm"
                    >
                      <Icon name="Plus" size={18} />
                      <span>Nova Reserva</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Reservation Modal */}
      <ReservationModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
        }}
        reservation={selectedReservation}
        onSave={handleSaveReservation}
        mode={modalMode}
        loading={loadingStates.edit}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        reservation={reservationToDelete}
        isDeleting={isDeleting}
      />

      {/* Notification Modal */}
      <NotificationModal
        isOpen={showNotification}
        onClose={handleCloseNotification}
        type={notificationData.type}
        title={notificationData.title}
        message={notificationData.message}
        details={notificationData.details}
      />

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        reservation={reservationForPayment}
        onProcessPayment={handleProcessPaymentSubmit}
        loading={loadingStates.processPayment?.[reservationForPayment?.id] || false}
      />

      {/* Modal de Check-in */}
      {showCheckinModal && checkinReservation && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-card border border-border rounded-lg w-full max-w-2xl shadow-elevation-3 my-8">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/20 rounded-lg">
                  <Icon name="LogIn" size={24} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-foreground">
                    Confirmar Check-in
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Confirmar chegada do hóspede
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => {
                setShowCheckinModal(false);
                setCheckinReservation(null);
              }}>
                <Icon name="X" size={20} />
              </Button>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="space-y-4">
                {/* Informações da Reserva */}
                <div className="bg-muted/30 rounded-lg p-4">
                  <h4 className="font-medium text-foreground mb-3">Detalhes da Reserva</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-muted-foreground block mb-1">Código:</span>
                      <span className="font-medium text-foreground">
                        {checkinReservation.reservationCode}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground block mb-1">Hóspede:</span>
                      <span className="font-medium text-foreground">
                        {checkinReservation.guestName}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground block mb-1">Quarto:</span>
                      <span className="font-medium text-foreground">
                        {checkinReservation.room?.number || checkinReservation.roomNumber || 'N/A'} - {translateRoomType(checkinReservation.room?.type || checkinReservation.roomType)}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground block mb-1">Check-in Previsto:</span>
                      <span className="font-medium text-foreground">
                        {new Date(checkinReservation.checkInDate).toLocaleDateString('pt-BR')} às {new Date(checkinReservation.checkInDate).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground block mb-1">Check-out Previsto:</span>
                      <span className="font-medium text-foreground">
                        {checkinReservation.checkOutDate ? 
                          `${new Date(checkinReservation.checkOutDate).toLocaleDateString('pt-BR')} às ${new Date(checkinReservation.checkOutDate).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : 
                          'N/A'
                        }
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground block mb-1">Valor Total:</span>
                      <span className="font-semibold text-lg text-foreground">
                        R$ {parseFloat(checkinReservation.totalAmount || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Aviso */}
                <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Icon name="Info" size={20} className="text-primary mt-0.5" />
                    <div>
                      <h4 className="font-medium text-primary mb-1">
                        Confirmar Chegada
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        Esta ação irá confirmar a chegada do hóspede e marcar o quarto como ocupado.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-6 border-t border-border">
              <div className="text-sm text-muted-foreground">
                O status da reserva será atualizado para "Check-in Realizado"
              </div>
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowCheckinModal(false);
                    setCheckinReservation(null);
                  }}
                  disabled={checkinLoading}
                  className="flex-1 sm:flex-none"
                >
                  Cancelar
                </Button>
                <Button 
                  variant="default"
                  onClick={confirmCheckIn}
                  disabled={checkinLoading}
                  className="flex-1 sm:flex-none bg-primary hover:bg-primary/90"
                >
                  {checkinLoading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Processando...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center space-x-2">
                      <Icon name="LogIn" size={16} />
                      <span>Confirmar Check-in</span>
                    </div>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Checkout */}
      {showCheckoutModal && checkoutReservation && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-card border border-border rounded-lg w-full max-w-2xl shadow-elevation-3 my-8 max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-warning/20 rounded-lg">
                  <Icon name="LogOut" size={24} className="text-warning" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-foreground">
                    Confirmar Checkout
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Liberar quarto e finalizar estadia
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => {
                setShowCheckoutModal(false);
                setCheckoutReservation(null);
              }}>
                <Icon name="X" size={20} />
              </Button>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="space-y-4">
                {/* Informações da Reserva */}
                <div className="bg-muted/30 rounded-lg p-4">
                  <h4 className="font-medium text-foreground mb-3">Detalhes da Reserva</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-muted-foreground block mb-1">Código:</span>
                      <span className="font-medium text-foreground">
                        {checkoutReservation.reservationCode}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground block mb-1">Hóspede:</span>
                      <span className="font-medium text-foreground">
                        {checkoutReservation.guestName}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground block mb-1">Quarto:</span>
                      <span className="font-medium text-foreground">
                        {checkoutReservation.room?.number || checkoutReservation.roomNumber || 'N/A'} - {translateRoomType(checkoutReservation.room?.type || checkoutReservation.roomType)}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground block mb-1">Check-in:</span>
                      <span className="font-medium text-foreground">
                        {new Date(checkoutReservation.checkInDate).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground block mb-1">Check-out:</span>
                      <span className="font-medium text-foreground">
                        {checkoutReservation.checkOutDate ? 
                          new Date(checkoutReservation.checkOutDate).toLocaleDateString('pt-BR') : 
                          'Hoje'
                        }
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground block mb-1">Valor Total:</span>
                      {(() => {
                        const original = Number(checkoutReservation.totalAmount || 0);
                        const discount = checkoutReservation.discountAmount 
                          ? (typeof checkoutReservation.discountAmount === 'string' 
                              ? parseFloat(checkoutReservation.discountAmount) 
                              : Number(checkoutReservation.discountAmount))
                          : 0;
                        const finalAmount = original - discount;
                        if (discount > 0) {
                          return (
                            <div>
                              <div className="font-semibold text-lg text-success">
                                {finalAmount.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </div>
                              <div className="text-xs text-muted-foreground line-through">
                                {original.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (- {discount.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} desconto)
                              </div>
                            </div>
                          );
                        }
                        return (
                          <span className="font-semibold text-lg text-foreground">
                            R$ {original.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        );
                      })()}
                    </div>
                    <div className="md:col-span-2">
                      <span className="text-muted-foreground block mb-1">Status do Pagamento:</span>
                      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                        checkoutReservation.paymentStatus === 'PAID' ? 
                          'bg-success/20 text-success' : 
                          'bg-warning/20 text-warning'
                      }`}>
                        {checkoutReservation.paymentStatus === 'PAID' ? '✓ Pago' : '⏳ Pendente'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Opção de Checkout Faturado - Apenas se pagamento estiver pendente */}
                {checkoutReservation.paymentStatus !== 'PAID' && (
                  <div className="flex items-center gap-3 p-4 bg-muted/30 border border-border rounded-lg">
                    <input
                      type="checkbox"
                      id="deferredPaymentCheckbox"
                      checked={isDeferredPayment}
                      onChange={(e) => setIsDeferredPayment(e.target.checked)}
                      className="w-4 h-4 text-primary border-border rounded focus:ring-primary cursor-pointer"
                    />
                    <label 
                      htmlFor="deferredPaymentCheckbox" 
                      className="text-sm font-semibold text-foreground cursor-pointer select-none"
                    >
                      🧾 Faturar estadia / Checkout sem pagamento imediato (Pagamento a prazo)
                    </label>
                  </div>
                )}

                {/* Campos de Pagamento - Apenas se pagamento estiver pendente E não for faturado */}
                {checkoutReservation.paymentStatus !== 'PAID' && !isDeferredPayment && (
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h4 className="font-medium text-foreground mb-4 flex items-center gap-2">
                      <Icon name="CreditCard" size={18} className="text-primary" />
                      Dados do Pagamento
                    </h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Valor */}
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Valor Total *
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={checkoutPaymentData.amount}
                          onChange={(e) => setCheckoutPaymentData(prev => ({ ...prev, amount: e.target.value }))}
                          className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                          placeholder="0.00"
                        />
                      </div>

                      {/* Forma de Pagamento */}
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Forma de Pagamento *
                        </label>
                        <select
                          value={checkoutPaymentData.paymentMethod}
                          onChange={(e) => setCheckoutPaymentData(prev => ({ ...prev, paymentMethod: e.target.value }))}
                          className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                        >
                          <option value="CASH">💵 Dinheiro</option>
                          <option value="CREDIT_CARD">💳 Cartão de Crédito</option>
                          <option value="DEBIT_CARD">💳 Cartão de Débito</option>
                          <option value="PIX">📱 PIX</option>
                          <option value="BANK_TRANSFER">🏦 Transferência Bancária</option>
                        </select>
                      </div>

                      {/* ID da Transação */}
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          ID da Transação (Opcional)
                        </label>
                        <input
                          type="text"
                          value={checkoutPaymentData.transactionId}
                          onChange={(e) => setCheckoutPaymentData(prev => ({ ...prev, transactionId: e.target.value }))}
                          className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                          placeholder="Ex: 123456789"
                        />
                      </div>

                      {/* Data do Pagamento */}
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Data do Pagamento *
                        </label>
                        <input
                          type="date"
                          value={checkoutPaymentData.paymentDate}
                          onChange={(e) => setCheckoutPaymentData(prev => ({ ...prev, paymentDate: e.target.value }))}
                          className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                        />
                      </div>

                      {/* Desconto */}
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Desconto (R$)
                        </label>
                        <input
                          type="text"
                          value={checkoutPaymentData.discountAmount}
                          onChange={(e) => {
                            // Formatação automática de moeda
                            let value = e.target.value;
                            if (value) {
                              const numbers = value.replace(/\D/g, '');
                              if (numbers !== '') {
                                let cleanedNumbers = numbers;
                                if (cleanedNumbers.length > 1 && cleanedNumbers[0] === '0') {
                                  cleanedNumbers = cleanedNumbers.substring(1);
                                }
                                const numberValue = parseFloat(cleanedNumbers) / 100;
                                value = numberValue.toFixed(2).replace('.', ',');
                              } else {
                                value = '';
                              }
                            }
                            setCheckoutPaymentData(prev => ({ ...prev, discountAmount: value }));
                          }}
                          className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                          placeholder="0,00"
                        />
                      </div>

                      {/* Observações */}
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Observações (Opcional)
                        </label>
                        <textarea
                          value={checkoutPaymentData.notes}
                          onChange={(e) => setCheckoutPaymentData(prev => ({ ...prev, notes: e.target.value }))}
                          className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                          rows="2"
                          placeholder="Ex: Pago em espécie, com desconto de 10%"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Observações para faturamento */}
                {checkoutReservation.paymentStatus !== 'PAID' && isDeferredPayment && (
                  <div className="bg-card border border-border rounded-lg p-4">
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Observações de Faturamento (Opcional)
                    </label>
                    <textarea
                      value={checkoutPaymentData.notes}
                      onChange={(e) => setCheckoutPaymentData(prev => ({ ...prev, notes: e.target.value }))}
                      className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                      rows="2"
                      placeholder="Ex: Faturado para a empresa X com vencimento em 15 dias"
                    />
                  </div>
                )}

                {/* Aviso */}
                <div className={`${
                  checkoutReservation.paymentStatus !== 'PAID'
                    ? isDeferredPayment
                      ? 'bg-primary/10 border border-primary/20'
                      : 'bg-primary/10 border border-primary/20'
                    : 'bg-warning/10 border border-warning/20'
                } rounded-lg p-4`}>
                  <div className="flex items-start gap-3">
                    <Icon 
                      name={
                        checkoutReservation.paymentStatus !== 'PAID' 
                          ? isDeferredPayment 
                            ? 'FileText' 
                            : 'CreditCard' 
                          : 'AlertTriangle'
                      } 
                      size={20} 
                      className={`${
                        checkoutReservation.paymentStatus !== 'PAID' 
                          ? 'text-primary' 
                          : 'text-warning'
                      } mt-0.5`} 
                    />
                    <div>
                      <h4 className={`font-medium mb-1 ${
                        checkoutReservation.paymentStatus !== 'PAID' 
                          ? 'text-primary' 
                          : 'text-warning'
                      }`}>
                        {checkoutReservation.paymentStatus !== 'PAID' 
                          ? isDeferredPayment 
                            ? 'Checkout Faturado' 
                            : 'Pagamento Pendente' 
                          : 'Atenção'}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {checkoutReservation.paymentStatus !== 'PAID' ? (
                          isDeferredPayment ? (
                            <>
                              <strong>O quarto será liberado e a estadia finalizada.</strong>
                              <span className="block mt-1">
                                O pagamento continuará pendente (faturado). Você poderá registrar a liquidação do valor posteriormente pela tela de reservas.
                              </span>
                            </>
                          ) : (
                            <>
                              <strong>O pagamento ainda não foi processado.</strong>
                              <span className="block mt-1">
                                Preencha os dados de pagamento acima para concluir o checkout.
                              </span>
                              <span className="block mt-1">
                                Esta ação irá registrar o pagamento e liberar o quarto automaticamente.
                              </span>
                            </>
                          )
                        ) : (
                          <>
                            Esta ação irá finalizar a estadia e liberar o quarto. 
                            <span className="block mt-1">
                              Certifique-se de que todos os procedimentos de checkout foram realizados.
                            </span>
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-6 border-t border-border">
              <div className="text-sm text-muted-foreground text-center sm:text-left">
                Esta ação não pode ser desfeita
              </div>
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowCheckoutModal(false);
                    setCheckoutReservation(null);
                  }}
                  disabled={checkoutLoading}
                  className="flex-1 sm:flex-none"
                >
                  Cancelar
                </Button>
                <Button 
                  variant="default"
                  onClick={confirmCheckout}
                  disabled={checkoutLoading}
                  className={`flex-1 sm:flex-none ${
                    checkoutReservation.paymentStatus !== 'PAID' 
                      ? isDeferredPayment 
                        ? "bg-primary hover:bg-primary/90 text-white" 
                        : "bg-success hover:bg-success/90 text-white" 
                      : "bg-warning hover:bg-warning/90 text-warning-foreground"
                  }`}
                >
                  {checkoutLoading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Processando...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center space-x-2">
                      <Icon 
                        name={
                          checkoutReservation.paymentStatus !== 'PAID' 
                            ? isDeferredPayment 
                              ? 'FileText' 
                              : 'Check' 
                            : 'LogOut'
                        } 
                        size={16} 
                      />
                      <span className="hidden sm:inline">
                        {checkoutReservation.paymentStatus !== 'PAID' 
                          ? isDeferredPayment 
                            ? 'Faturar e Confirmar Checkout' 
                            : 'Processar Pagamento e Checkout' 
                          : 'Confirmar Checkout'}
                      </span>
                      <span className="sm:hidden">
                        {checkoutReservation.paymentStatus !== 'PAID' 
                          ? isDeferredPayment 
                            ? 'Faturar Checkout' 
                            : 'Pagar e Checkout' 
                          : 'Checkout'}
                      </span>
                    </div>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReservationsPage;